import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { 
  MapPin, 
  Building2, 
  Calendar,
  ArrowRight,
  ExternalLink,
  CheckCircle2,
  Clock,
  TrendingUp
} from 'lucide-react';

const ProyectosPage = () => {
  const [filtro, setFiltro] = useState('todos');

  const proyectos = [
    {
      id: 1,
      nombre: 'Torre Reforma Elite',
      ubicacion: 'Paseo de la Reforma, CDMX',
      tipo: 'Residencial Vertical',
      estado: 'en_construccion',
      estadoLabel: 'En Construcción',
      imagen: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=800',
      descripcion: 'Desarrollo residencial de lujo con 180 departamentos, amenidades de primer nivel y ubicación privilegiada.',
      metros: '45,000 m²',
      unidades: 180,
      entrega: 'Q4 2026',
      inversion: true,
      rendimiento: '14.5%'
    },
    {
      id: 2,
      nombre: 'Plaza Comercial Satélite',
      ubicacion: 'Ciudad Satélite, Edo. de México',
      tipo: 'Comercial',
      estado: 'completado',
      estadoLabel: 'Completado',
      imagen: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=800',
      descripcion: 'Centro comercial con 85 locales, área de food court y estacionamiento para 500 vehículos.',
      metros: '28,000 m²',
      unidades: 85,
      entrega: 'Entregado 2024',
      inversion: false
    },
    {
      id: 3,
      nombre: 'Residencial Los Álamos',
      ubicacion: 'Querétaro, Qro.',
      tipo: 'Residencial Horizontal',
      estado: 'en_construccion',
      estadoLabel: 'En Construcción',
      imagen: 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=800',
      descripcion: 'Fraccionamiento privado con 120 casas, casa club, áreas verdes y seguridad 24/7.',
      metros: '65,000 m²',
      unidades: 120,
      entrega: 'Q2 2026',
      inversion: true,
      rendimiento: '12.8%'
    },
    {
      id: 4,
      nombre: 'Corporativo Santa Fe',
      ubicacion: 'Santa Fe, CDMX',
      tipo: 'Oficinas',
      estado: 'planeacion',
      estadoLabel: 'En Planeación',
      imagen: 'https://images.unsplash.com/photo-1554469384-e58fac16e23a?w=800',
      descripcion: 'Torre de oficinas clase A+ con certificación LEED, 25 pisos y helipuerto.',
      metros: '55,000 m²',
      unidades: 25,
      entrega: 'Q1 2028',
      inversion: true,
      rendimiento: '16.2%'
    },
    {
      id: 5,
      nombre: 'Hotel Boutique Polanco',
      ubicacion: 'Polanco, CDMX',
      tipo: 'Hospitalidad',
      estado: 'completado',
      estadoLabel: 'Completado',
      imagen: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=800',
      descripcion: 'Hotel boutique de 45 habitaciones con restaurante gourmet y rooftop bar.',
      metros: '8,500 m²',
      unidades: 45,
      entrega: 'Entregado 2023',
      inversion: false
    },
    {
      id: 6,
      nombre: 'Parque Industrial Bajío',
      ubicacion: 'León, Guanajuato',
      tipo: 'Industrial',
      estado: 'en_construccion',
      estadoLabel: 'En Construcción',
      imagen: 'https://images.unsplash.com/photo-1587293852726-70cdb56c2866?w=800',
      descripcion: 'Parque industrial con 12 naves, infraestructura de clase mundial y acceso a autopista.',
      metros: '120,000 m²',
      unidades: 12,
      entrega: 'Q3 2026',
      inversion: true,
      rendimiento: '13.5%'
    }
  ];

  const filtros = [
    { id: 'todos', label: 'Todos' },
    { id: 'en_construccion', label: 'En Construcción' },
    { id: 'completado', label: 'Completados' },
    { id: 'planeacion', label: 'En Planeación' },
    { id: 'inversion', label: 'Con Inversión Abierta' }
  ];

  const proyectosFiltrados = proyectos.filter(p => {
    if (filtro === 'todos') return true;
    if (filtro === 'inversion') return p.inversion;
    return p.estado === filtro;
  });

  const getEstadoBadge = (estado) => {
    switch (estado) {
      case 'completado':
        return <Badge className="bg-emerald-500/10 text-emerald-500 border-emerald-500/20">Completado</Badge>;
      case 'en_construccion':
        return <Badge className="bg-blue-500/10 text-blue-500 border-blue-500/20">En Construcción</Badge>;
      case 'planeacion':
        return <Badge className="bg-amber-500/10 text-amber-500 border-amber-500/20">En Planeación</Badge>;
      default:
        return <Badge variant="outline">{estado}</Badge>;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative py-24 lg:py-32 bg-gradient-to-b from-zinc-900 to-background">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto">
            <Badge variant="outline" className="mb-6 text-xs uppercase tracking-widest">
              Nuestros Proyectos
            </Badge>
            <h1 className="heading-display mb-6">
              Portafolio de<br />
              <span className="text-muted-foreground">Desarrollos</span>
            </h1>
            <p className="text-lg text-muted-foreground leading-relaxed">
              Más de 15 años de experiencia desarrollando proyectos inmobiliarios 
              de alta calidad en las mejores ubicaciones de México.
            </p>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-12 border-b border-border">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-3xl font-light text-foreground mb-2">+50</div>
              <div className="text-sm text-muted-foreground">Proyectos Completados</div>
            </div>
            <div>
              <div className="text-3xl font-light text-foreground mb-2">+500,000</div>
              <div className="text-sm text-muted-foreground">m² Desarrollados</div>
            </div>
            <div>
              <div className="text-3xl font-light text-foreground mb-2">+2,500</div>
              <div className="text-sm text-muted-foreground">Unidades Entregadas</div>
            </div>
            <div>
              <div className="text-3xl font-light text-foreground mb-2">15+</div>
              <div className="text-sm text-muted-foreground">Años de Experiencia</div>
            </div>
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="py-8 border-b border-border bg-muted/30">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-wrap justify-center gap-2">
            {filtros.map((f) => (
              <button
                key={f.id}
                onClick={() => setFiltro(f.id)}
                className={`px-4 py-2 rounded-sm text-sm transition-all ${
                  filtro === f.id
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-background text-muted-foreground hover:text-foreground border border-border'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {proyectosFiltrados.map((proyecto) => (
              <Card key={proyecto.id} className="card-dashboard overflow-hidden group">
                <div className="relative aspect-[4/3] overflow-hidden">
                  <img
                    src={proyecto.imagen}
                    alt={proyecto.nombre}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute top-4 left-4 flex gap-2">
                    {getEstadoBadge(proyecto.estado)}
                    {proyecto.inversion && (
                      <Badge className="bg-primary/90 text-primary-foreground">
                        <TrendingUp className="h-3 w-3 mr-1" />
                        Inversión Abierta
                      </Badge>
                    )}
                  </div>
                </div>
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="text-lg font-medium">{proyecto.nombre}</h3>
                    <Badge variant="outline" className="text-xs">{proyecto.tipo}</Badge>
                  </div>
                  
                  <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
                    <MapPin className="h-4 w-4" strokeWidth={1.5} />
                    {proyecto.ubicacion}
                  </div>
                  
                  <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                    {proyecto.descripcion}
                  </p>
                  
                  <div className="grid grid-cols-3 gap-4 py-4 border-t border-border text-center">
                    <div>
                      <div className="text-sm font-medium">{proyecto.metros}</div>
                      <div className="text-xs text-muted-foreground">Superficie</div>
                    </div>
                    <div>
                      <div className="text-sm font-medium">{proyecto.unidades}</div>
                      <div className="text-xs text-muted-foreground">Unidades</div>
                    </div>
                    <div>
                      <div className="text-sm font-medium">{proyecto.entrega}</div>
                      <div className="text-xs text-muted-foreground">Entrega</div>
                    </div>
                  </div>

                  {proyecto.inversion && (
                    <div className="pt-4 border-t border-border">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-sm text-muted-foreground">Rendimiento Proyectado</span>
                        <span className="text-lg font-medium text-primary">{proyecto.rendimiento}</span>
                      </div>
                      <Link to="/oportunidades">
                        <Button className="w-full gap-2" size="sm">
                          Ver Oportunidad de Inversión
                          <ArrowRight className="h-4 w-4" />
                        </Button>
                      </Link>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-zinc-900">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="heading-section mb-6">
            ¿Tienes un terreno o proyecto?
          </h2>
          <p className="text-muted-foreground mb-8 max-w-2xl mx-auto">
            Si tienes un terreno con potencial de desarrollo o un proyecto que requiere 
            financiamiento, nos encantaría conocerlo.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/aportar-inmueble">
              <Button size="lg" className="gap-2">
                Aportar un Inmueble
                <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
            <Link to="/contacto">
              <Button size="lg" variant="outline" className="gap-2">
                Contactar al Equipo
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProyectosPage;
